'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface ContactHeroProps {
  className?: string;
}

const ContactHero = ({ className = '' }: ContactHeroProps) => {
  const { language, t } = useLanguage();

  return (
    <section className={`relative bg-gradient-to-br from-primary via-secondary to-brand-ocean text-white py-16 lg:py-24 overflow-hidden ${className}`}>
      {/* Lighthouse Beam Effect */}
      <div className="absolute inset-0 lighthouse-beam opacity-20"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Icon name="ClockIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-body font-medium">{t({ en: 'Available 24/7 for Your Moving Needs', ar: 'متاح على مدار الساعة لاحتياجات النقل الخاصة بك' })}</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-headline font-bold mb-6 leading-tight">
            {t({ en: 'Get in Touch with ', ar: 'تواصل مع ' })}<span className="text-accent">{t({ en: 'Almanara Moving', ar: 'المنارة للنقل' })}</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg lg:text-xl text-white/90 mb-8 max-w-2xl mx-auto font-body leading-relaxed">
            {t({ en: 'Your trusted moving partner in Kuwait. We\'re here to answer your questions, provide instant quotes, and schedule your seamless move.', ar: 'شريكك الموثوق للنقل في الكويت. نحن هنا للإجابة على أسئلتك وتقديم عروض أسعار فورية وجدولة نقلك بسلاسة.' })}
          </p>

          {/* Quick Contact Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:69911181"
              className="flex items-center space-x-3 bg-white text-primary px-8 py-4 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-elevated w-full sm:w-auto justify-center group"
            >
              <Icon name="PhoneIcon" size={24} variant="solid" className="group-hover:scale-110 transition-transform duration-base" />
              <span className="font-accent text-lg">6991 1181</span>
            </a>
            
            <a
              href="https://wa.me/96569911181"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 bg-success text-white px-8 py-4 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-elevated w-full sm:w-auto justify-center group"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" className="group-hover:scale-110 transition-transform duration-base" />
              <span>{t({ en: 'WhatsApp Us', ar: 'واتساب' })}</span>
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-white/80">
            <div className="flex items-center space-x-2">
              <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-accent" />
              <span>{t({ en: 'Fully Insured', ar: 'مؤمن بالكامل' })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="ClockIcon" size={20} variant="solid" className="text-accent" />
              <span>{t({ en: '24/7 Support', ar: 'دعم على مدار الساعة' })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="StarIcon" size={20} variant="solid" className="text-accent" />
              <span>{t({ en: '5-Star Rated', ar: 'تقييم 5 نجوم' })}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactHero;